// Confirmation and rotation modal for Inbound Webhook credentials (WP-14).
import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import {
  rotateWebhookWorkflowToken,
  rotateWebhookWorkflowSecret,
  type WebhookWorkflowDoc,
} from '../../lib/webhookWorkflows';

interface WorkflowRotateModalProps {
  open: boolean;
  onClose: () => void;
  workflow: WebhookWorkflowDoc | null;
}

export function WorkflowRotateModal({ open, onClose, workflow }: WorkflowRotateModalProps) {
  const { t } = useI18n();

  const [target, setTarget] = useState<'token' | 'secret'>('token');
  const [rotating, setRotating] = useState(false);
  const [newCredential, setNewCredential] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!workflow) return null;

  const handleRotate = async () => {
    setRotating(true);
    setError(null);
    try {
      if (target === 'token') {
        const token = await rotateWebhookWorkflowToken(workflow.id);
        setNewCredential(token);
      } else {
        const secret = await rotateWebhookWorkflowSecret(workflow.id);
        setNewCredential(secret);
      }
    } catch (err: any) {
      setError(err.message || t('webhook.rotate.failedError'));
    } finally {
      setRotating(false);
    }
  };

  const copyToClipboard = async () => {
    if (!newCredential) return;
    try {
      await navigator.clipboard.writeText(newCredential);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // noop
    }
  };

  const handleClose = () => {
    setNewCredential(null);
    setCopied(false);
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('webhook.rotate.modalTitle') + ': ' + workflow.name}
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        {newCredential ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              {target === 'token'
                ? t('webhook.rotate.tokenRotatedSuccess')
                : t('webhook.rotate.secretRotatedSuccess')}
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              {t('webhook.modal.copyWarning')}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                {target === 'token' ? t('webhook.modal.authToken') : t('webhook.modal.signingSecret')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={newCredential}
                  className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono dark:border-slate-700 dark:bg-slate-900"
                />
                <Button variant="secondary" size="sm" onClick={copyToClipboard}>
                  {copied ? t('common.copied') : t('common.copy')}
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t border-slate-200 dark:border-slate-800">
              <Button variant="primary" onClick={handleClose}>
                {t('webhook.modal.doneButton')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              {t('webhook.rotate.description')}
            </p>

            <div className="space-y-2">
              <label className="flex items-start gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <input
                  type="radio"
                  name="rotateTarget"
                  value="token"
                  checked={target === 'token'}
                  onChange={() => setTarget('token')}
                  className="mt-0.5 accent-primary"
                />
                <div>
                  <b className="text-xs text-slate-900 dark:text-white block">
                    {t('webhook.rotate.rotateAuthToken')}
                  </b>
                  <span className="text-[11px] text-slate-400">
                    {t('webhook.rotate.rotateAuthTokenDesc')}
                  </span>
                </div>
              </label>

              <label className="flex items-start gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <input
                  type="radio"
                  name="rotateTarget"
                  value="secret"
                  checked={target === 'secret'}
                  onChange={() => setTarget('secret')}
                  className="mt-0.5 accent-primary"
                />
                <div>
                  <b className="text-xs text-slate-900 dark:text-white block">
                    {t('webhook.rotate.rotateSigningSecret')}
                  </b>
                  <span className="text-[11px] text-slate-400">
                    {t('webhook.rotate.rotateSigningSecretDesc')}
                  </span>
                </div>
              </label>
            </div>

            <div className="rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
              <strong>{t('webhook.modal.warning')}:</strong> {t('webhook.rotate.warningInvalidation')}
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
              <Button variant="ghost" onClick={handleClose} disabled={rotating}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleRotate} disabled={rotating}>
                {rotating ? t('common.loading') : t('webhook.rotate.confirmButton')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
